import React, { useState } from 'react';
import { Users } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface AudienceDistributionProps {
  data?: any; // In production, this would be typed based on backend response
}

const AudienceDistribution: React.FC<AudienceDistributionProps> = ({ data }) => {
  const [selectedDimension, setSelectedDimension] = useState<'age' | 'gender' | 'device'>('age');

  // Sample audience distribution data (in production, this would come from backend via data prop)
  const audienceDistribution = {
    age: [
      { category: '18-24', audience: 25000, sales: 125000 },
      { category: '25-34', audience: 42000, sales: 315000 },
      { category: '35-44', audience: 38000, sales: 380000 },
      { category: '45-54', audience: 28000, sales: 280000 },
      { category: '55+', audience: 17000, sales: 136000 },
    ],
    gender: [
      { category: 'Male', audience: 75000, sales: 600000 },
      { category: 'Female', audience: 75000, sales: 636000 },
    ],
    device: [
      { category: 'Mobile', audience: 90000, sales: 675000 },
      { category: 'Desktop', audience: 45000, sales: 450000 },
      { category: 'Tablet', audience: 15000, sales: 111000 },
    ],
  };

  const dimensions = [
    { key: 'age' as const, label: 'Age Group', icon: '👥' },
    { key: 'gender' as const, label: 'Gender', icon: '⚧' },
    { key: 'device' as const, label: 'Device Type', icon: '📱' },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Users className="w-4 h-4 mr-2 text-blue-500" />
        Audience Distribution
      </h4>

      <div className="flex gap-6">
        {/* Vertical Tabs */}
        <div className="flex flex-col gap-2 min-w-[140px]">
          {dimensions.map((dim) => (
            <button
              key={dim.key}
              onClick={() => setSelectedDimension(dim.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                selectedDimension === dim.key
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              <span>{dim.icon}</span>
              <span>{dim.label}</span>
            </button>
          ))}
        </div>

        {/* Chart Area */}
        <div className="flex-1">
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={audienceDistribution[selectedDimension]} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
              <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 12 }} />
              <YAxis dataKey="category" type="category" tick={{ fill: '#6B7280', fontSize: 12 }} width={80} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#fff',
                }}
                formatter={(value: any) => value.toLocaleString()}
              />
              <Legend />
              <Bar dataKey="audience" fill="#3B82F6" name="Audience" radius={[0, 8, 8, 0]} />
              <Bar dataKey="sales" fill="#6B7280" name="Sales ($)" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AudienceDistribution;
